import GeoImageSource from 'ol-ext/source/GeoImage';
import GeoImageLayer from 'ol-ext/layer/GeoImage';

/**
 * GeoImage 생성 관련 기능을 담당하는 클래스
 */
export class GeoImageCreator {
  /**
   * GeoImageSource 생성
   * @param {string} imageUrl - 이미지 URL
   * @param {Array} center - 중심점 [lng, lat]
   * @param {Array} scale - 스케일 [x, y]
   * @param {number} rotation - 회전각도 (라디안)
   * @returns {GeoImageSource} 생성된 GeoImageSource
   */
  createGeoImageSource(imageUrl, center, scale = [1, 1], rotation = 0) {
    return new GeoImageSource({
      url: imageUrl,
      imageCenter: center,
      imageScale: scale,
      imageRotate: rotation,
      projection: 'EPSG:4326' // WGS84
    });
  }

  /**
   * GeoImageLayer 생성
   * @param {GeoImageSource} source - GeoImageSource
   * @param {Object} options - 레이어 옵션
   * @returns {GeoImageLayer} 생성된 GeoImageLayer
   */
  createGeoImageLayer(source, options = {}) {
    const defaultOptions = {
      name: 'GeoImage',
      opacity: 1.0,
      zIndex: 100,
      ...options
    };

    return new GeoImageLayer({
      name: defaultOptions.name,
      opacity: defaultOptions.opacity,
      source: source,
      zIndex: defaultOptions.zIndex
    });
  }

  /**
   * 이미지 크기와 맵 크기를 기반으로 최적 스케일 계산
   * @param {Object} imageSize - 이미지 크기 {width, height}
   * @param {Array} mapSize - 맵 크기 [width, height]
   * @param {number} resolution - 맵 해상도
   * @param {number} targetMapPercent - 목표 맵 차지 비율 (기본값: 0.3)
   * @returns {number} 최적 스케일
   */
  calculateOptimalImageScale(imageSize, mapSize, resolution, targetMapPercent = 0.3) {
    if (!imageSize || !mapSize || !resolution) {
      console.warn('GeoImageCreator: 유효하지 않은 매개변수');
      return 1;
    }

    // 목표: 이미지가 맵의 targetMapPercent 정도 차지하도록
    const targetPixelWidth = mapSize[0] * targetMapPercent;
    const targetPixelHeight = mapSize[1] * targetMapPercent;

    // 목표 맵 단위 크기
    const targetMapWidth = targetPixelWidth * resolution;
    const targetMapHeight = targetPixelHeight * resolution;

    // 필요한 스케일 계산
    const neededScaleX = targetMapWidth / imageSize.width;
    const neededScaleY = targetMapHeight / imageSize.height;

    // 비율을 유지하면서 더 작은 스케일 사용
    const optimalScale = Math.min(neededScaleX, neededScaleY);

    console.log('GeoImageCreator: 최적 스케일 계산:', {
      imageSize: `${imageSize.width}x${imageSize.height}`,
      mapSize: `${mapSize[0]}x${mapSize[1]}`,
      resolution,
      targetMapPercent,
      optimalScale
    });

    return optimalScale;
  }

  /**
   * GeoImageSource에 스케일 적용
   * @param {GeoImageSource} source - GeoImageSource
   * @param {Array|number} scale - 스케일 [x, y] 또는 단일 값
   */
  applyScaleToGeoImageSource(source, scale) {
    if (!source) {
      console.warn('GeoImageCreator: 유효하지 않은 GeoImageSource');
      return;
    }

    const scaleArray = Array.isArray(scale) ? scale : [scale, scale];
    source.setScale(scaleArray);

    console.log('GeoImageCreator: 스케일 적용 완료:', scaleArray);
  }

  /**
   * GeoImageSource에 중심점 적용
   * @param {GeoImageSource} source - GeoImageSource
   * @param {Array} center - 중심점 [lng, lat]
   */
  applyCenterToGeoImageSource(source, center) {
    if (!source || !center) {
      console.warn('GeoImageCreator: 유효하지 않은 매개변수');
      return;
    }

    source.setCenter(center);
    console.log('GeoImageCreator: 중심점 적용 완료:', center);
  }

  /**
   * GeoImageSource에 회전 적용
   * @param {GeoImageSource} source - GeoImageSource
   * @param {number} rotation - 회전각도 (라디안)
   */
  applyRotationToGeoImageSource(source, rotation) {
    if (!source) {
      console.warn('GeoImageCreator: 유효하지 않은 GeoImageSource');
      return;
    }

    source.setRotation(rotation);
    console.log(`GeoImageCreator: 회전 적용 완료: ${rotation * 180 / Math.PI}도`);
  }

  /**
   * GeoImageSource에서 현재 설정 가져오기
   * @param {GeoImageSource} source - GeoImageSource
   * @returns {Object} 현재 설정
   */
  getGeoImageSourceSettings(source) {
    if (!source) {
      console.warn('GeoImageCreator: 유효하지 않은 GeoImageSource');
      return null;
    }

    return {
      center: source.getCenter(),
      scale: source.getScale(),
      rotation: source.getRotation(),
      extent: source.calculateExtent(true)
    };
  }
}
